import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import { bootstrapUtils } from "react-bootstrap/lib/utils";
import { filter, isNil, find, map } from "lodash";
import styled from "styled-components";

import EventEmitter from "../../../services/event-emitter";
import { batchRequest, getRequest, postRequest } from "../../../api/ApiService";
import { logError } from "../../../services/utils";
import Loader from "react-loaders";
import NumberFormat from "react-number-format";
import {
  incrementUnacceptedDecisions,
  decrementUnacceptedDecisions
} from "../../../api/SubmitDecisions";
import WarningAlert from '../../alerts/warning';
import Fragment from "react-dot-fragment";
import SuccessAlert from '../../alerts/success';

bootstrapUtils.addStyle(Button, "save");

const AVAILABLE_MACHINES_URL = `${process.env.REACT_APP_API_URL}static/machines/`;
const TEAM_MACHINES_URL = `${process.env.REACT_APP_API_URL}team/machines/`;
const FACTORIES_URL = `${process.env.REACT_APP_API_URL}team/factories/`;
const PRODUCT_MANUFACTURES_URL = `${process.env.REACT_APP_API_URL}team/product_manufactures/`;

const FONT = "'Mukta Vaani', Lato, sans-serif";

const ModalTitle = styled.h3`
  font-family: ${FONT};
  font-weight: 700;
  font-size: 22px;
  line-height: 28px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const ModalDescription = styled.p`
  font-family: ${FONT};
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.7);
  margin: 8px 0 20px;

  strong {
    color: rgba(0, 27, 59, 0.94);
    font-weight: 700;
  }
`;

const ModernTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${FONT};
  margin: 0;
`;

const HeadRow = styled.tr`
  border-bottom: 1px solid #ECEDEF;
`;

const HeadCell = styled.th`
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
  text-align: ${p => p.align || 'left'};
  padding: 12px 16px 10px 0;
  letter-spacing: 0.3px;
  text-transform: uppercase;

  &:last-child {
    padding-right: 0;
  }
`;

const Row = styled.tr`
  border-bottom: 1px solid #F5F6F7;

  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.td`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  text-align: ${p => p.align || 'left'};
  padding: 14px 16px 14px 0;
  vertical-align: middle;
  font-variant-numeric: tabular-nums;

  &:last-child {
    padding-right: 0;
  }
`;

const ActionBase = styled(Button)`
  font-family: ${FONT};
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  padding: 6px 18px;
  border-radius: 6px;
  border: none;
  min-width: 86px;
  float: none !important;
  text-align: center;
`;

const LoadingButton = styled(ActionBase)`
  background-color: #F5F6F7;
  border: 1px solid #D5D9DD;
  color: rgba(0, 27, 59, 0.4);
  cursor: not-allowed;
`;
const UndoSellButton = styled(ActionBase)`
  background-color: #fff;
  color: #2A4169;
  border: 1px solid #2A4169;
  &:hover, &:focus, &:active {
    background-color: #F5F6F7;
    color: #2A4169;
    border: 1px solid #2A4169;
    outline: none;
  }
`;
const SellButton = styled(ActionBase)`
  background-color: #D34C4C;
  color: #fff;
  &:hover, &:focus, &:active {
    background-color: #c43c3c;
    color: #fff;
    border: none;
    outline: none;
  }
`;
const BuyButton = styled(ActionBase)`
  background-color: #2A4169;
  color: #fff;
  &:hover, &:focus, &:active {
    background-color: #36507e;
    color: #fff;
    border: none;
    outline: none;
  }
`;

// Wrapper + tooltip for the disabled Buy button on Production Line 4
// in Year 1 (locked until later rounds). Same pattern as the tooltips
// in PDDecisions / PMDecisions — the disabled button's pointer-events
// are killed so hover reliably fires on the wrapper.
const BuyTooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;

  &:hover > span.buy-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const BuyTooltip = styled.span`
  position: absolute;
  bottom: 100%;
  right: 0;
  transform: translateY(-8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: ${FONT};
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 14px;
  border-radius: 6px;
  width: 260px;
  text-align: left;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: 32px;
    border: 5px solid transparent;
    border-top-color: rgba(0, 27, 59, 0.94);
  }
`;

const DisabledBuyButton = styled(BuyButton)`
  opacity: 0.5;
  pointer-events: none;
`;

const FooterCloseButton = styled.button`
  background: transparent;
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  padding: 8px 22px;
  font-family: ${FONT};
  font-weight: 500;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.8);
  cursor: pointer;

  &:hover {
    background: #F5F6F7;
  }
`;

class BuyingMachines extends Component {
  constructor(props) {
    super(props);

    this.state = {
      show: false,
      loading: true,
      available_machines: [],
      team_machines: [],
      loading_machines: {},
      product_manufactures: [],
      factory: 0,
      showSellConfirmation: false,
      tmp_object_id: 0,
      tmp_machine_id: 0,
      tmp_prev_round: false,
      sellConfirmationMachine: null,
      sellConfirmationLoss: 0,
      sellConfirmationHasProduction: false,
      successMessage: null,
    };

    this.eventemitter = EventEmitter.getInstance();
  }

  showSuccessMessage = successMessage => {
    this.setState({
      successMessage,
    }, () => {
      this.hideSuccessMessage();
    });
  }

  hideSuccessMessage = () => {
    setTimeout(() => {
      this.setState({
        successMessage: null,
      });
    }, 5000);
  }

  componentDidMount() {
    this.eventemitter.on("show-buying-machines-modal", this.showModal);
    this.eventemitter.on("hide-buying-machines-modal", this.hideModal);
  }

  componentWillUnmount() {
    this.eventemitter.off("show-buying-machines-modal", this.showModal);
    this.eventemitter.off("hide-buying-machines-modal", this.hideModal);
  }

  showModal = () => {
    this.setState(
      {
        show: true
      },
      () => {
        this.fetchData();
      }
    );
  };

  hideModal = () => {
    this.setState({
      show: false
    });
  };

  showNpvModal = () => {
    this.setState(
      {
        show: false
      },
      () => {
        this.eventemitter.trigger("show-npv-modal");
      }
    );
  };

  reloadData = () => {
    const { reloadData } = this.props;
    const { loading_machines } = this.state;

    reloadData();

    getRequest(TEAM_MACHINES_URL)
      .then(res => {
        this.setState({
          team_machines: filter(
            res.body,
            machine => machine.team === this.props.teamId
          ),
          loading_machines: map(loading_machines, () => false)
        });
      })
      .catch(err => {
        logError(err, "BuyingMachines", "reloadData");
      });
  };

  fetchData = () => {
    const joinRequest = batchRequest([
      AVAILABLE_MACHINES_URL,
      TEAM_MACHINES_URL,
      FACTORIES_URL,
      PRODUCT_MANUFACTURES_URL
    ]);

    joinRequest
      .then(res => {
        if (
          res[0].statusCode !== 200 ||
          res[1].statusCode !== 200 ||
          res[2].statusCode !== 200 ||
          res[3].statusCode !== 200
        ) {
          throw res;
        }

        const available_machines = res[0].body;
        const team_machines = res[1].body;
        const factories = res[2].body;
        const factory = factories.length > 0 ? factories[0].id : 0;
        const product_manufactures = res[3].body;

        this.setState({
          available_machines,
          team_machines: filter(
            team_machines,
            machine => machine.team === this.props.teamId
          ),
          factory,
          product_manufactures,
          loading: false
        });
      })
      .catch(err => {
        logError(err, "BuyingMachines", "fetchData");
      });
  };

  haveMachine = machineId => {
    const { team_machines } = this.state;

    return !isNil(team_machines.find(i => i.machine === machineId));
  };

  isSoldMachine = machine_id => {
    return !isNil(
      find(
        this.state.team_machines,
        machine => machine.id === machine_id && machine.accepted === false
      )
    );
  };

  isLoadingMachine = machine_id => {
    const { loading_machines } = this.state;

    if (machine_id in loading_machines) {
      return loading_machines[machine_id];
    }

    return false;
  };

  createMachineRequest = machine => {
    const { factory } = this.state;

    return postRequest(`${process.env.REACT_APP_API_URL}team/machines/`).send({
      factory,
      machine
    });
  };

  onCreateMachine = machine_id => {
    const { tourMode, currentRound, onFirstMachineBought } = this.props;

    if (tourMode === true) {
      return;
    }

    // Capture BEFORE the POST: is this the team's first-ever machine
    // in Round 1? Used to fire the coaching popup once, on the very
    // first purchase.
    const wasFirstMachineRd1 =
      Number(currentRound) === 1
      && Array.isArray(this.state.team_machines)
      && this.state.team_machines.length === 0;

    this.setState({
      loading_machines: { ...this.state.loading_machines, [machine_id]: true }
    });

    this.createMachineRequest(machine_id)
      .then(res => {
        const foundMachine = this.state.available_machines.find(i => i.id === machine_id);
        this.showSuccessMessage(`Production Line ${foundMachine.name} selected successfully`);
        incrementUnacceptedDecisions("operations");
        this.reloadData();
        if (wasFirstMachineRd1 && onFirstMachineBought) {
          onFirstMachineBought();
        }
      })
      .catch(err => {
        logError(err, "BuyingMachines", "onCreateMachine");
      });
  };

  deleteMachineRequest = machine_id => {
    return postRequest(
      `${process.env.REACT_APP_API_URL}team/machines/${machine_id}/sell/`
    );
  };

  onDeleteMachine = machineId => {
    const { tourMode, currentRound, rounds } = this.props;
    const { team_machines, product_manufactures } = this.state;

    if (tourMode === true) {
      return;
    }

    const obj = team_machines.find(i => i.machine === machineId);
    if (!obj) {
      return;
    }

    // Does the machine have any live production assignments? Used
    // only to decide which confirmation copy to show — BE handles the
    // cascade delete when the machine is sold, so the FE no longer
    // needs the specific production ids. units_produced=0 rows don't
    // tie up the line and are intentionally excluded.
    const hasLiveProduction = product_manufactures.some(
      i => Number(i.machine) === Number(obj.id)
        && Number(i.units_produced) > 0
    );

    const round = rounds.find(i => i.id === obj.round);
    const is_prev_round = !!round && round.number !== currentRound;

    this.setState({
      loading_machines: { ...this.state.loading_machines, [machineId]: true }
    });

    // Three paths:
    //  - Prior-round sell: always show the confirmation (loss on sale,
    //    plus the cascade warning when production is attached).
    //  - Same-round Undo Buy with production: show the undo-buy warning
    //    so the student is aware the production rows will go with it.
    //  - Same-round Undo Buy without production: direct delete, no modal.
    if (is_prev_round || hasLiveProduction) {
      this.setState(
        {
          tmp_object_id: obj.id,
          tmp_machine_id: machineId,
          tmp_prev_round: is_prev_round,
          sellConfirmationHasProduction: hasLiveProduction,
        },
        () => {
          this.showSellConfirmation(machineId);
        }
      );
    } else {
      this.onDeleteMachineRequest(obj.id, machineId, is_prev_round);
    }
  };

  // Loss on sale = (a - b) * (1 - c) where
  //   a = machine cost (from rich team-decisions team_machines.cost)
  //   b = sum of machine_depreciations.value for this machine across
  //       every round EXCEPT the current one
  //   c = selling_price_factors.sales_value matched by the machine's age
  computeLossOnSale = (teamMachineRecordId) => {
    const {
      richTeamMachines = [],
      machineDepreciations = [],
      sellingPriceFactors = [],
      currentRound,
      rounds = [],
    } = this.props;

    const machine = richTeamMachines.find(m => Number(m.id) === Number(teamMachineRecordId));
    if (!machine) return 0;

    const a = Number(machine.cost) || 0;

    // machine_depreciations entries reference a round id (rd) — map our
    // current round number to its id so we can exclude it from the sum.
    const currentRoundObj = rounds.find(r => Number(r.number) === Number(currentRound));
    const currentRoundId = currentRoundObj ? Number(currentRoundObj.id) : null;

    const b = machineDepreciations
      .filter(d =>
        Number(d.machine) === Number(teamMachineRecordId)
        && Number(d.rd) !== currentRoundId
      )
      .reduce((sum, d) => sum + (Number(d.value) || 0), 0);

    const age = Number(machine.age) || 0;
    const factor = sellingPriceFactors.find(f => Number(f.age) === age);
    const c = factor ? (Number(factor.sales_value) || 0) : 0;

    const loss = (a - b) * (1 - c);
    return loss > 0 ? loss : 0;
  };

  showSellConfirmation = (machineId) => {
    let sellConfirmationMachine = null;
    if (machineId) {
      const foundMachine = this.state.available_machines.find(i => i.id === machineId);
      if (foundMachine) {
        sellConfirmationMachine = foundMachine.name;
      }
    }

    const sellConfirmationLoss = this.computeLossOnSale(this.state.tmp_object_id);

    this.setState({
      showSellConfirmation: true,
      sellConfirmationMachine,
      sellConfirmationLoss,
    });
  };

  cancelSellConfirmation = () => {
    const { tmp_machine_id } = this.state;

    this.setState({
      showSellConfirmation: false,
      sellConfirmationHasProduction: false,
      loading_machines: {
        ...this.state.loading_machines,
        [tmp_machine_id]: false
      }
    });
  };

  doSellConfirmation = () => {
    const { tmp_object_id, tmp_machine_id, tmp_prev_round } = this.state;

    // Production rows attached to this machine are cascade-deleted by
    // the backend as part of the sell call — the FE just has to warn
    // the student and fire the sell.
    this.onDeleteMachineRequest(tmp_object_id, tmp_machine_id, tmp_prev_round);
  };

  // Reverses a prior-round machine sell that was made in the current
  // round. Hits POST /api/team/machines/{id}/unsell/ where {id} is the
  // team_machine record id (not the machine line number).
  onUndoSell = (machineId) => {
    const { tourMode, currentRound, rounds } = this.props;
    const { team_machines } = this.state;

    if (tourMode === true) return;

    const currentRoundObj = (rounds || []).find(r => Number(r.number) === Number(currentRound));
    const currentRoundId = currentRoundObj ? Number(currentRoundObj.id) : null;

    const obj = team_machines.find(m =>
      Number(m.machine) === Number(machineId)
      && Number(m.sold_in_round) === currentRoundId
    );
    if (!obj) return;

    this.setState({
      loading_machines: { ...this.state.loading_machines, [machineId]: true }
    });

    postRequest(`${process.env.REACT_APP_API_URL}team/machines/${obj.id}/unsell/`)
      .then(() => {
        const foundMachine = this.state.available_machines.find(i => i.id === machineId);
        const name = foundMachine ? foundMachine.name : machineId;
        this.showSuccessMessage(`Production Line ${name} sale was undone successfully`);
        // Selling decremented the unaccepted-decisions counter, so
        // undoing the sell increments it back for balance.
        incrementUnacceptedDecisions("operations");
        this.reloadData();
      })
      .catch(err => {
        logError(err, "BuyingMachines", "onUndoSell");
        this.setState({
          loading_machines: { ...this.state.loading_machines, [machineId]: false }
        });
      });
  };

  onDeleteMachineRequest = (object_id, machine_id, is_prev_round) => {
    this.deleteMachineRequest(object_id)
      .then(res => {
        const foundMachine = this.state.available_machines.find(i => i.id === machine_id);
        this.showSuccessMessage(`Production Line ${foundMachine.name} was de-selected successfully`);
        decrementUnacceptedDecisions("operations");
        this.reloadData();

        let loading_machines = { ...this.state.loading_machines };
        loading_machines[machine_id] = false;

        this.setState({
          //loading_machines,
          showSellConfirmation: false,
          sellConfirmationHasProduction: false,
        });
      })
      .catch(err => {
        logError(err, "BuyingMachines", "onDeleteMachine");
      });
  };

  getBuyMachineButtonClassname = machine_id => {
    const base_classname = "btn-factory-modal btn btn-danger btn-xs";

    if (this.haveMachine(machine_id) || this.isSoldMachine(machine_id)) {
      return `${base_classname} btn-opd-step-passed`;
    }

    return base_classname;
  };

  getSellMachineButtonClassname = machine_id => {
    const base_classname = "btn-factory-modal btn btn-danger btn-xs";

    if (this.isSoldMachine(machine_id)) {
      return `${base_classname} btn-opd-step-passed`;
    }

    return base_classname;
  };

  isBuyMachineButtonDisabled = machine_id =>
    this.isLoadingMachine(machine_id) || this.haveMachine(machine_id);

  isSellMachineButtonDisabled = machine_id =>
    this.isLoadingMachine(machine_id) || !this.haveMachine(machine_id);

  // True when the machine was purchased in the current round — the
  // user can just "undo" the buy (no depreciation loss, no
  // confirmation modal). Prior-round purchases render as "Sell" and
  // route through the loss-on-sale confirmation flow.
  purchasedThisRound = machine_id => {
    const { currentRound, rounds } = this.props;
    const { team_machines } = this.state;
    const currentRoundObj = (rounds || []).find(
      r => Number(r.number) === Number(currentRound)
    );
    const currentRoundId = currentRoundObj ? Number(currentRoundObj.id) : null;
    if (currentRoundId == null) return false;
    const obj = team_machines.find(m => Number(m.machine) === Number(machine_id));
    if (!obj) return false;
    return Number(obj.round) === currentRoundId;
  };

  soldOldMachine = machine_id => {
    const { currentRound, rounds } = this.props;
    const { team_machines } = this.state;
    const currentRoundId = find(rounds, round => round.number === currentRound)[
      "id"
    ];
    const soldMachine = find(
      team_machines,
      machine =>
        machine["sold_in_round"] === currentRoundId &&
        machine.machine === machine_id
    );

    return !isNil(soldMachine);
  };

  getButton = (machineId, lineIndex) => {
    if (this.isLoadingMachine(machineId)) {
      return <LoadingButton disabled>Loading...</LoadingButton>;
    }
    if (this.soldOldMachine(machineId)) {
      return (
        <UndoSellButton onClick={() => this.onUndoSell(machineId)}>
          Undo Sell
        </UndoSellButton>
      );
    }
    if (this.haveMachine(machineId)) {
      // Same-round buys are cheap to reverse (no loss on sale, no
      // confirmation) — render as "Undo" with the neutral outline
      // style, mirroring the existing Undo-Sell affordance. Prior-
      // round buys stay red "Sell" so the destructive action reads
      // clearly and still triggers the confirmation modal.
      if (this.purchasedThisRound(machineId)) {
        return (
          <UndoSellButton onClick={() => this.onDeleteMachine(machineId)}>
            Undo Buy
          </UndoSellButton>
        );
      }
      return (
        <SellButton onClick={() => this.onDeleteMachine(machineId)}>
          Sell
        </SellButton>
      );
    }

    // Production Line 4 (index 3) is locked in Year 1 — its higher
    // capital cost + output is intended to unlock in later rounds.
    if (Number(this.props.currentRound) === 1 && lineIndex === 3) {
      return (
        <BuyTooltipWrapper style={{ cursor: 'not-allowed' }}>
          <DisabledBuyButton disabled>Buy</DisabledBuyButton>
          <BuyTooltip className="buy-tooltip">
            Production Line 4 isn't available in Year 1. You'll be able to purchase it in later years.
          </BuyTooltip>
        </BuyTooltipWrapper>
      );
    }

    return (
      <BuyButton onClick={() => this.onCreateMachine(machineId)}>Buy</BuyButton>
    );
  };

  getMachineCosts = costs => {
    return `$${(costs / 1000000).toFixed(2)}M`;
  };

  render() {
    const {
      show,
      loading,
      available_machines,
      showSellConfirmation,
      sellConfirmationLoss,
      sellConfirmationHasProduction,
      tmp_prev_round,
      successMessage,
    } = this.state;
    const hideModal = this.hideModal;

    // Message copy depends on which action triggered the confirmation.
    //   - Prior-round Sell, no production: loss-on-sale only (original).
    //   - Prior-round Sell with production: loss-on-sale + cascade warning.
    //   - Same-round Undo Buy with production: cascade warning only.
    const formattedLoss = `$${Math.round(sellConfirmationLoss || 0).toLocaleString('en-US')}`;
    const hasProduction = !!sellConfirmationHasProduction;
    let message;
    if (tmp_prev_round && hasProduction) {
      message = (
        <span>
          Selling this production line will result in a loss on sale of <strong>{formattedLoss}</strong> and will delete any existing production currently assigned to the machine. Are you sure?
        </span>
      );
    } else if (tmp_prev_round) {
      message = (
        <span>
          Selling this machine will result in a loss on sale of <strong>{formattedLoss}</strong>. Are you sure?
        </span>
      );
    } else {
      message = (
        <span>
          You currently have production scheduled on this production line. Undoing the purchase will remove those production units. Are you sure?
        </span>
      );
    }

    return show ? (
      <Fragment>
        {showSellConfirmation && (
          <WarningAlert
            title="Heads Up!"
            message={message}
            onSuccess={this.doSellConfirmation}
            onCancel={this.cancelSellConfirmation}
          />
        )}
        {!showSellConfirmation && (
          <Modal
            show={true}
            onHide={hideModal}
            dialogClassName="modal-dialog-bigger"
          >
            <Modal.Body>
              <ModalTitle>Machine Selection</ModalTitle>
              <ModalDescription>
                Choose which production machines to add to or remove from your facility.
                Each line has a different output rate and capital cost, so pick the mix
                that fits your production goals for the year. <strong>Units Per Hour</strong> is
                each machine's maximum hourly output.
              </ModalDescription>
              {successMessage && <SuccessAlert
                message={successMessage}
              />}
              <ModernTable>
                <colgroup>
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '25%' }} />
                </colgroup>
                {loading === false && (
                  <thead>
                    <HeadRow>
                      <HeadCell>Production Line</HeadCell>
                      <HeadCell align="center">Units Per Hour</HeadCell>
                      <HeadCell align="center">Cost</HeadCell>
                      <HeadCell align="center">Action</HeadCell>
                    </HeadRow>
                  </thead>
                )}
                <tbody>
                  {loading === true ? (
                    <tr>
                      <td className="loader-inline" colSpan="4" style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Loader type="ball-scale-ripple" active={true} />
                      </td>
                    </tr>
                  ) : (
                    available_machines.map((machine, i) => (
                      <Row key={i}>
                        <Cell>Production Line {i + 1}</Cell>
                        <Cell align="center">
                          <NumberFormat
                            value={machine.units_per_hour}
                            suffix=" units"
                            thousandSeparator=","
                            displayType="text"
                          />
                        </Cell>
                        <Cell align="center">{this.getMachineCosts(machine.costs)}</Cell>
                        <Cell align="center">{this.getButton(machine.id, i)}</Cell>
                      </Row>
                    ))
                  )}
                </tbody>
              </ModernTable>
            </Modal.Body>
            <Modal.Footer bsClass="modal-footer op-decision-modal-footer">
              <FooterCloseButton onClick={hideModal}>Close</FooterCloseButton>
            </Modal.Footer>
          </Modal>
        )}
      </Fragment>
    ) : null;
  }
}

export default BuyingMachines;



// WEBPACK FOOTER //
// ./src/components/operations/helper-components/buying-machines.js